import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { saveCredsInLocalStorage, claimPendingAttribution } from "../services/AuthService";
import { getUser } from "../services/UserService";
import { consumePendingAuthIntent, getAuthReturnRetailer } from "../utils/authReturn";
import { defaultPostAuthPath, heldPostAuthPath } from "../utils/dealOnboarding";
import { track } from "../utils/analytics";
import { clearSignupReferrer } from "../services/AttributionService";

const errorMessages: Record<string, { title: string; message: string }> = {
  no_email: {
    title: "Email Required",
    message: "Your Discord account doesn't have a verified email. Please add and verify an email in your Discord settings, then try again.",
  },
  discord_auth_failed: {
    title: "Authentication Failed",
    message: "Discord authentication was denied or failed. Please try again.",
  },
};

export default function DiscordCallbackPage() {
  const [searchParams] = useSearchParams();
  const [errorInfo, setErrorInfo] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    const complete = async () => {
      const accessToken = searchParams.get("access_token");
      const refreshToken = searchParams.get("refresh_token");
      const error = searchParams.get("error");

      if (error) {
        console.error("Discord OAuth error:", error);
        if (errorMessages[error]) {
          setErrorInfo(errorMessages[error]);
          return;
        }
        window.location.href = `/login?error=${encodeURIComponent(error)}`;
        return;
      }

      if (accessToken && refreshToken) {
        saveCredsInLocalStorage(accessToken, refreshToken);
        const newSignup = searchParams.get("new_signup") === "1";
        await claimPendingAttribution(newSignup, 1500, newSignup);
        if (!newSignup) clearSignupReferrer();
        const user = await getUser();
        if (newSignup) {
          track("signup", { method: "discord", source: "oauth" });
        }
        const held = heldPostAuthPath(user);
        if (held) {
          window.location.href = held;
          return;
        }
        const intent = consumePendingAuthIntent(defaultPostAuthPath(user));
        const destination = intent.returnTo;
        if (destination !== "/") {
          track("guest_intent_resumed", { surface: "web", retailer: getAuthReturnRetailer(destination), trigger: "oauth_callback", source: intent.source || "discord" });
        }
        window.location.href = destination;
        return;
      }

      window.location.href = "/login?error=missing_tokens";
    };

    void complete();
  }, [searchParams]);

  if (errorInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-dark-900 dark:via-dark-950 dark:to-dark-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{errorInfo.title}</h2>
          <p className="text-gray-600 dark:text-dark-300 mb-6">{errorInfo.message}</p>
          <a
            href="/get-started"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors"
          >
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-dark-900 dark:via-dark-950 dark:to-dark-900 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-dark-300">Completing Discord login...</p>
      </div>
    </div>
  );
}
